import { Reveal, SectionHeader } from "./Reveal";
import { PhotoMark } from "./PhotoMark";

const ABOUT_IMG =
  "https://images.pexels.com/photos/34036536/pexels-photo-34036536.jpeg?auto=compress&cs=tinysrgb&w=1200";

const CHAPTERS = [
  {
    num: "01",
    title: "El alma del bolero",
    body: "Cuarto Bolero revive la época dorada del bolero con una mezcla única de tradición y frescura. Un espectáculo en vivo y sin artificios, diseñado para emocionar a todos los públicos, donde cada acorde y cada voz resuenan con sentimiento.",
  },
  {
    num: "02",
    title: "Casi tres décadas en escena",
    body: "Desde su creación en 1997, el grupo deja huella en cada presentación. Sus actuaciones no solo evocan recuerdos: crean momentos inolvidables que perduran en el alma de quienes los escuchan.",
  },
  {
    num: "03",
    title: "«Con un solo corazón»",
    body: "CUARTO BOLERO transporta al público a la época dorada de la música latina con un repertorio que combina los grandes clásicos del bolero, composiciones originales y piezas icónicas del cancionero cubano. Además, es un show divertido, muy participativo, buscando la conexión permanente con el público.",
    list: [
      { song: "Sabor a mí", composer: "Álvaro Carrillo" },
      { song: "Si tú me dices ven", composer: "Alfredo “Güero” Gil" },
      { song: "Contigo", composer: "Claudio Estrada" },
      { song: "Historia de un amor", composer: "Carlos Eleta Almarán" },
      { song: "Guantanamera", composer: "Joselito Fernández" },
      { song: "Lágrimas negras", composer: "Miguel Matamoros" },
    ],
    closing:
      "Y así, hasta una hora y media de música sin interrupciones, repasando la historia del bolero y el son con sus canciones inolvidables. Además, el grupo se adapta a cada ocasión, ofreciendo setlists personalizados para eventos especiales, asegurando una experiencia única y hecha a medida para su público.",
  },
];

const STATS = [
  { value: "1997", label: "Fundación" },
  { value: "+400", label: "Conciertos" },
  { value: "2", label: "Álbumes" },
  { value: "90'", label: "De espectáculo" },
];

const FEATURED_MEMBERS = [
  {
    name: "Jesús Alvarez",
    role: "Guitarra y voz",
    photo: "/photos/jesus-alvarez.jpg",
    bio: [
      "Guitarrista de gran trayectoria, apasionado por la música acústica y con una vida dedicada a los escenarios, su talento lo ha llevado a formar parte de distintas agrupaciones, desde tunas hasta conjuntos vocales, enriqueciendo su estilo y sensibilidad artística.",
      "Con una voz inconfundible que da vida al bolero y un toque magistral en la guitarra, su interpretación destila emoción y elegancia. Cada acorde y cada nota lo convierten en el alma indiscutible del grupo.",
    ],
  },
  {
    name: "Cheni Rodríguez",
    role: "Percusión y voz",
    photo: "/photos/cheni-rodriguez.jpg",
    bio: [
      "Percusionista con el ritmo en la sangre, Cheni da vida a cada actuación de Cuarto Bolero con su maestría en las maracas y su inconfundible voz grave y poderosa.",
      "Su talento y pasión se reflejan en cada interpretación, aportando fuerza y autenticidad al sonido del grupo.",
    ],
  },
  {
    name: "Javier Salinas",
    role: "Requinto y dirección musical",
    photo: "/photos/javier-salinas.jpg",
    photoWrap: "md:col-span-7",
    textWrap: "md:col-span-5",
    imgAspect: "aspect-[3/2]",
    bio: [
      "Músico de nacimiento, ha dedicado su vida a los escenarios, formando parte de numerosas agrupaciones ligadas al folclore y a la tradición de los tríos. Con una amplia trayectoria como guitarrista en tunas y tríos de toda España, hoy lidera la dirección musical de Cuarto Bolero, aportando su maestría y sensibilidad artística.",
      "Su dominio del requinto se convierte en el sello distintivo del grupo, llevando cada interpretación a otro nivel.",
    ],
  },
  {
    name: "Andrés Esteban",
    role: "Bajo",
    photo: "/photos/andres-esteban.png",
    photoWrap: "md:col-span-5",
    textWrap: "md:col-span-7",
    imgAspect: "aspect-[4/5]",
    bio: [
      "Músico vocacional, este bajista español tiene recursos con su instrumento para exprimirlo en cada actuación.",
      "Su manera de entender la música hace de Andrés un pilar fundamental en el sonido del grupo.",
    ],
  },
  {
    name: "Eddy Ricardo",
    role: "Percusión · En nuestros directos",
    photo: "/photos/eddy-ricardo.png",
    photoWrap: "md:col-span-5",
    textWrap: "md:col-span-7",
    imgAspect: "aspect-[3/4]",
    bio: [
      "Este cubano es todo un artista en la percusión y le aporta al grupo todo el frenético ritmo que necesita, especialmente en el son cubano.",
      "Con una sensibilidad muy especial para los boleros, Eddy lleva en la sangre el talento.",
    ],
  },
];

const MEMBERS = [];

export const Manifesto = () => (
  <section id="banda" className="relative py-24 sm:py-32">
    <div className="mx-auto max-w-[1600px] px-6 sm:px-12 lg:px-24">
      <SectionHeader
        eyebrow="El Grupo"
        title={
          <>
            Cuando la música se convierte en <span className="italic text-gold">emoción</span>.
          </>
        }
      />

      <div className="mt-10 max-w-3xl">
        <Reveal delay={0.1}>
          <p className="text-base md:text-lg leading-relaxed text-sand">
            Con casi 30 años de trayectoria, CUARTO BOLERO ha cautivado al público en escenarios de toda España, con especial presencia en Castilla-La Mancha, Madrid y Extremadura. Su impecable recorrido incluye más de 400 actuaciones en directo, dos discos editados y numerosas colaboraciones con otros artistas.
          </p>
          <p className="mt-4 text-base md:text-lg leading-relaxed text-sand">
            Su talento los ha llevado a participar en programas de radio y televisión, así como en eventos privados y celebraciones especiales, consolidándose como uno de los grupos más representativos del bolero en España.
          </p>
        </Reveal>
      </div>

      <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-20">
        <div className="md:col-span-5">
          <div className="md:sticky md:top-28">
            <Reveal>
              <div className="relative">
                <div className="absolute -inset-3 border border-gold/25 pointer-events-none" aria-hidden="true" />
                <img src={ABOUT_IMG} alt="Guitarra española en directo" className="w-full aspect-[4/5] object-cover" data-testid="about-image" />
                <PhotoMark className="bottom-4 right-4 h-12 w-auto opacity-90" />
              </div>
              <p className="mt-8 font-serif italic text-lg text-sand">“Déjate seducir por la elegancia del bolero en su estado más puro.”</p>
            </Reveal>
          </div>
        </div>

        <div className="md:col-span-7">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.num} delay={i * 0.05}>
              <div className={`py-10 first:pt-0 ${i > 0 ? "hairline" : ""}`} data-testid={`manifesto-chapter-${c.num}`}>
                <span className="font-serif text-6xl md:text-7xl leading-none text-gold/25">{c.num}</span>
                <h3 className="mt-4 font-serif text-2xl md:text-3xl text-cream">{c.title}</h3>
                <p className="mt-4 text-base md:text-lg leading-relaxed text-sand">{c.body}</p>
                {c.list && (
                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3" data-testid={`chapter-list-${c.num}`}>
                    {c.list.map((s) => (
                      <li key={s.song} className="flex items-baseline gap-2 text-base text-sand">
                        <span className="text-gold">“{s.song}”</span>
                        <span className="text-sm text-mute">— {s.composer}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {c.closing && (
                  <p className="mt-6 text-base md:text-lg leading-relaxed text-sand">{c.closing}</p>
                )}
              </div>
            </Reveal>
          ))}

          <div className="hairline grid grid-cols-2 md:grid-cols-4 gap-8 py-12" data-testid="band-stats">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.06}>
                <p className="font-serif text-4xl md:text-5xl text-gold">{s.value}</p>
                <p className="mt-2 text-[11px] uppercase tracking-[0.25em] text-mute">{s.label}</p>
              </Reveal>
            ))}
          </div>

          <div className="hairline pt-12">
            <Reveal>
              <h3 className="font-serif text-3xl text-cream">Los músicos</h3>
            </Reveal>

            {FEATURED_MEMBERS.map((m, fi) => (
              <Reveal key={m.name} delay={0.05}>
                <div data-testid={`featured-member-${fi + 1}`} className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-start">
                  <div className={`relative ${m.photoWrap || "md:col-span-7"} ${fi % 2 === 1 ? "md:order-2" : ""}`}>
                    <div className="absolute inset-0 translate-x-3 translate-y-3 border border-gold/30" aria-hidden="true" />
                    <img src={m.photo} alt={m.name} className={`relative w-full object-cover ${m.imgAspect || "aspect-[3/2]"}`} data-testid={`featured-member-photo-${fi + 1}`} />
                    <PhotoMark className="bottom-4 right-4 h-12 w-auto opacity-90" />
                  </div>
                  <div className={`${m.textWrap || "md:col-span-5"} ${fi % 2 === 1 ? "md:order-1" : ""}`}>
                    <p className="eyebrow">{m.role}</p>
                    <h4 className="mt-3 font-serif text-3xl md:text-4xl text-cream">{m.name}</h4>
                    {m.bio.map((p, i) => (
                      <p key={i} className="mt-5 text-base md:text-lg leading-relaxed text-sand">{p}</p>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}

            {MEMBERS.length > 0 && (
              <div className="mt-12">
                {MEMBERS.map((m, i) => (
                  <Reveal key={m.name} delay={i * 0.04}>
                    <div
                      data-testid={`member-row-${i + 1}`}
                      className="group flex items-baseline gap-5 border-t border-cream/10 py-5 transition-all duration-300 hover:bg-cream/[0.02] hover:pl-3"
                    >
                      <span className="text-xs text-mute">0{i + 1}</span>
                      <div className="flex-1">
                        <p className="font-serif text-xl md:text-2xl text-cream group-hover:text-gold transition-colors">{m.name}</p>
                        <p className="mt-1 text-[11px] uppercase tracking-[0.22em] text-gold/80">{m.role}</p>
                      </div>
                      <p className="hidden md:block max-w-xs text-sm text-mute text-right">{m.note}</p>
                    </div>
                  </Reveal>
                ))}
                <div className="border-t border-cream/10" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  </section>
);
